/**
 * Live-sync tag registration per resource. Collections refresh lists;
 * detail is the per-record tag.
 *
 * @const
 * @type {!Object<string, {collections: !Array<string>, detail: string}>}
 */
export const resourceTags = {
  customer: { collections: ["Customer"], detail: "Customer" },
  customer_address: { collections: ["CustomerAddress"], detail: "CustomerAddress" },
  sales_rep: { collections: ["salesRep"], detail: "salesRep" },
  pricelist: { collections: ["Pricelist"], detail: "Pricelist" },
  currency: { collections: ["Currency"], detail: "Currency" },
  product: { collections: ["Product"], detail: "Product" },
  unit_of_measure: { collections: ["UnitOfMeasure"], detail: "UnitOfMeasure" },
  tax_rate: { collections: ["TaxRate"], detail: "TaxRate" },

  site: { collections: ["SiteList", "SiteSelection", "SiteCodeOptions"], detail: "SiteDetail" },
  facility: {
    collections: ["WarehouseList", "WarehouseSelection", "FacilityCodeOptions"],
    detail: "WarehouseDetail",
  },
  supplier: {
    collections: ["SupplierList", "SupplierSelection", "SupplierCodeOptions"],
    detail: "SupplierDetail",
  },
  payment_term: { collections: ["PaymentTerm"], detail: "PaymentTerm" },
  chart_account: { collections: ["ChartsAccount"], detail: "ChartsAccount" },
  sale_order: { collections: ["order"], detail: "orderDetail" },
  purchase_order: {
    collections: ["purchaseOrderList"],
    detail: "purchaseOrderDetail",
  },
  transfer_order: {
    collections: ["transferOrderList"],
    detail: "transferOrder",
  },
};

// Initial requests have not provided RTK tags yet. Register them as well so
// events arriving during the first fetch are reconciled after that fetch.
/**
 * RTK mutation endpoint names whose tags this tab already invalidated.
 * Matching resource-sync events are skipped in this tab only.
 *
 * @const
 * @type {Object<string, string>}
 */
export const mutationResources = {
  createCustomer: "customer",
  updateCustomer: "customer",
  createProduct: "product",
  editProduct: "product",
  createSite: "site",
  patchSite: "site",
  createWarehouse: "facility",
  updateWarehouse: "facility",
  createSuppliers: "supplier",
  updateSuppliers: "supplier",
  createTax: "tax_rate",
  updateTax: "tax_rate",
  createSalesRep: "sales_rep",
  updateSalesRep: "sales_rep",
  createChartsAccount: "chart_account",
  updateChartsAccount: "chart_account",
  createPaymentTerm: "payment_term",
  updatePaymentTerm: "payment_term",
  createPricelist: "pricelist",
  updatePricelist: "pricelist",
  createCustomerAddress: "customer_address",
  createSalesOrder: "sale_order",
  updateSalesOrder: "sale_order",
  patchSalesOrder: "sale_order",
  createPurchaseOrder: "purchase_order",
  updatePurchaseOrder: "purchase_order",
  createTransferOrder: "transfer_order",
  updateTransferOrder: "transfer_order",
  updateTransferOrderName: "transfer_order",
};

const OWN_EVENT_SKIP_MS = 2500;

export const resourceQueryTags = {
  getCustomers: ["Customer"],
  getCustomersSelection: ["Customer"],
  getCustomerById: ["Customer"],
  getCustomerCodeOptions: ["Customer"],
  getCustomerAddresses: ["CustomerAddress"],
  getSalesRepList: ["salesRep"],
  getPendingSalesRepInvitations: ["salesRep"],
  getSalesRepSelection: ["salesRep"],
  getSalesRepById: ["salesRep"],
  getPricelists: ["Pricelist"],
  getPriceListsSelection: ["Pricelist"],
  getPricelist: ["Pricelist"],
  getPricelistItems: ["Pricelist"],
  getPricelistFormulasSelection: ["Pricelist"],
  getPricelistFormula: ["Pricelist"],
  getCurrency: ["Currency"],
  getCurrencySelection: ["Currency"],
  getProducts: ["Product"],
  getProductsSelection: ["Product"],
  getProductForUpdate: ["Product"],
  getProductUnitPrice: ["Product"],
  getUnitOfMeasures: ["UnitOfMeasure"],
  getUnitOfMeasureSelection: ["UnitOfMeasure"],
  getUnitOfMeasureById: ["UnitOfMeasure"],
  getTax: ["TaxRate"],
  getTaxSelection: ["TaxRate"],
  getTaxById: ["TaxRate"],

  getSites: ["SiteList"],
  getSitesSelection: ["SiteSelection"],
  getSiteById: ["SiteDetail"],
  getSiteCodeOptions: ["SiteCodeOptions"],
  getWarehouse: ["WarehouseList"],
  getWarehouseSelection: ["WarehouseSelection"],
  getWarehouseById: ["WarehouseDetail"],
  getWarehouseDetail: ["WarehouseDetail"],
  getFacilityCodeOptions: ["FacilityCodeOptions"],
  getSuppliers: ["SupplierList"],
  getSuppliersSelection: ["SupplierSelection"],
  getSupplierById: ["SupplierDetail"],
  getSupplierCodeOptions: ["SupplierCodeOptions"],
  getChartsAccount: ["ChartsAccount"],
  getChartsAccountSelection: ["ChartsAccount"],
  getChartsAccountById: ["ChartsAccount"],
  getPaymentTerm: ["PaymentTerm"],
  getPaymentTermSelection: ["PaymentTerm"],
  getPaymentTermById: ["PaymentTerm"],
  getSalesOrderList: ["order"],
  getSalesOrderSelection: ["order"],
  getSalesOrderDetail: ["orderDetail"],
  getPurchaseOrders: ["purchaseOrderList"],
  getPurchaseOrdersSelection: ["purchaseOrderList"],
  getPurchaseOrderDetail: ["purchaseOrderDetail"],
  getTransferOrders: ["transferOrderList"],
  getTransferOrdersSelection: ["transferOrderList"],
};

/**
 * RTK tags to invalidate for a live-sync event.
 *
 * @param {string} resource Resource key, for example `customer`.
 * @param {(number|string)=} recordId When set, only that record's detail tag.
 * @return {!Array<(string|{type: string, id: (number|string)})>}
 */
export const tagsForResource = (resource, recordId) => {
  const registration = resourceTags[resource];
  if (!registration) return [];
  const id = recordId == null ? null : /^\d+$/.test(String(recordId)) ? Number(recordId) : recordId;
  const tags = [
    ...registration.collections,
    id == null ? registration.detail : { type: registration.detail, id },
  ];
  if (resource === "site" || resource === "supplier") {
    tags.push("WarehouseList", "WarehouseSelection", "WarehouseDetail");
  }
  const dependencies = {
    customer: ["CustomerAddress"],
    customer_address: ["Customer"],
    sales_rep: ["Customer"],
    pricelist: ["PricelistItem", "PricelistFormula", "Product"],
    currency: ["Customer", "Pricelist", "Product"],
    unit_of_measure: ["Product"],
    tax_rate: ["Product"],
  };
  tags.push(...(dependencies[resource] || []));
  return tags;
};

const newerRevision = (incoming, previous = "0") => {
  const next = String(incoming);
  const old = String(previous);
  return (
    /^\d+$/.test(next) && (next.length > old.length || (next.length === old.length && next > old))
  );
};

const nextRevision = revision => {
  const digits = String(revision).split("");
  for (let index = digits.length - 1; index >= 0; index--) {
    if (digits[index] !== "9") {
      digits[index] = String(Number(digits[index]) + 1);
      return digits.join("");
    }
    digits[index] = "0";
  }
  return `1${digits.join("")}`;
};

export const createResourceSyncMiddleware = ({
  api,
  socket,
  windowObject = window,
  documentObject = document,
  now = () => Date.now(),
}) => {
  let store;
  let identity;
  let generation = 0;
  let companyId;
  let revisions = {};
  let seen = new Set();
  let pending = new Map();
  let ownEvents = new Map();
  let batchTimer;
  let recoveryTimer;
  let snapshotRequest;
  let snapshotAgain = false;

  const skipOwnEvent = resource => (ownEvents.get(resource) || 0) > now();
  const rememberOwnMutation = resource => {
    if (resource) ownEvents.set(resource, now() + OWN_EVENT_SKIP_MS);
  };

  const flush = () => {
    batchTimer = undefined;
    if (pending.size) {
      const ready = [];
      const state = store.getState();
      pending.forEach((tag, key) => {
        const running = api.util
          .selectInvalidatedBy(state, [tag])
          .some(
            ({ endpointName, originalArgs }) =>
              api.endpoints[endpointName].select(originalArgs)(state).status === "pending",
          );
        const initialRunning = Object.values(state[api.reducerPath]?.queries || {}).some(
          query =>
            query.status === "pending" &&
            query.data === undefined &&
            resourceQueryTags[query.endpointName]?.includes(
              typeof tag === "string" ? tag : tag.type,
            ),
        );
        // RTK Query 1.9 can ignore a refetch while an older request is pending.
        // Wait for that request so its stale response cannot win over the event.
        if (!running && !initialRunning) {
          ready.push(tag);
          pending.delete(key);
        }
      });
      if (ready.length) store.dispatch(api.util.invalidateTags(ready));
      if (pending.size) batchTimer = setTimeout(flush, 50);
    }
  };
  const queue = tags => {
    tags.forEach(tag => pending.set(JSON.stringify(tag), tag));
    if (!batchTimer && pending.size) batchTimer = setTimeout(flush, 100);
  };
  const reconcile = async () => {
    if (!identity) return;
    if (snapshotRequest) {
      snapshotAgain = true;
      return;
    }
    const requestGeneration = generation;
    const request = store.dispatch(
      api.endpoints.getResourceRevisions.initiate(undefined, {
        forceRefetch: true,
      }),
    );
    snapshotRequest = request;
    try {
      const snapshot = await request.unwrap();
      if (requestGeneration !== generation) return;
      if (companyId !== undefined && String(companyId) !== String(snapshot.company_id)) {
        socket.reconnectWebSocket();
        store.dispatch(api.util.resetApiState());
        revisions = {};
        seen.clear();
      }
      companyId = snapshot.company_id;
      Object.entries(snapshot.revisions || {}).forEach(([resource, revision]) => {
        if (!resourceTags[resource]) return;
        if (revisions[resource] === undefined || newerRevision(revision, revisions[resource])) {
          queue(tagsForResource(resource));
        }
        if (revisions[resource] === undefined || newerRevision(revision, revisions[resource])) {
          revisions[resource] = String(revision);
        }
      });
    } catch (error) {
      // Temporary outages recover on the next focus/reconnect/periodic check.
    } finally {
      request.unsubscribe();
      if (snapshotRequest === request) snapshotRequest = undefined;
      if (requestGeneration === generation && snapshotAgain) {
        snapshotAgain = false;
        reconcile();
      }
    }
  };
  const onFocus = () => reconcile();
  const onVisible = () => {
    if (documentObject.visibilityState === "visible") reconcile();
  };
  const stop = () => {
    generation += 1;
    identity = undefined;
    socket.closeWebSocket();
    clearTimeout(batchTimer);
    clearInterval(recoveryTimer);
    snapshotRequest?.abort();
    snapshotRequest = undefined;
    snapshotAgain = false;
    batchTimer = undefined;
    pending.clear();
    seen.clear();
    ownEvents.clear();
    revisions = {};
    companyId = undefined;
    windowObject.removeEventListener("focus", onFocus);
    windowObject.removeEventListener("online", onFocus);
    documentObject.removeEventListener("visibilitychange", onVisible);
  };

  return middlewareStore => {
    store = middlewareStore;
    socket.setStore?.(store);
    return next => action => {
      const result = next(action);
      const auth = store.getState().auth;
      const nextIdentity =
        auth?.isAuthenticated && auth.user?.id
          ? `${auth.user.id}:${auth.user.company_id ?? auth.user.company ?? ""}`
          : undefined;
      if (nextIdentity !== identity) {
        const previousIdentity = identity;
        stop();
        identity = nextIdentity;
        if (previousIdentity && nextIdentity) store.dispatch(api.util.resetApiState());
        if (identity) {
          socket.createWebSocket();
          windowObject.addEventListener("focus", onFocus);
          windowObject.addEventListener("online", onFocus);
          documentObject.addEventListener("visibilitychange", onVisible);
          recoveryTimer = setInterval(() => {
            if (documentObject.visibilityState === "visible") reconcile();
          }, 120000);
          reconcile();
        }
      }
      if (action.type === "resourceSync/connected") reconcile();
      if (identity && action.type === `${api.reducerPath}/executeMutation/fulfilled`) {
        rememberOwnMutation(mutationResources[action.meta?.arg?.endpointName]);
      }
      if (identity && action.type === "resourceSync/event") {
        const event = action.payload;
        if (
          event?.schema_version !== 1 ||
          !resourceTags[event.resource] ||
          !event.event_id ||
          !/^\d+$/.test(String(event.resource_revision))
        )
          return result;
        if (companyId === undefined) {
          // Wait for the authenticated snapshot to establish the company scope.
          reconcile();
          return result;
        }
        if (String(event.company_id) !== String(companyId) || seen.has(event.event_id))
          return result;
        seen.add(event.event_id);
        if (seen.size > 5000) seen.delete(seen.values().next().value);
        // Even an older event refreshes: its publication cleared server caches
        // after a snapshot may already have observed the committed revision.
        const previous = revisions[event.resource];
        const missedChanges =
          previous === undefined ||
          (newerRevision(event.resource_revision, previous) &&
            String(event.resource_revision) !== nextRevision(previous));
        if (!skipOwnEvent(event.resource)) {
          queue(tagsForResource(event.resource, missedChanges ? undefined : event.record_id));
        }
        if (newerRevision(event.resource_revision, revisions[event.resource])) {
          revisions[event.resource] = String(event.resource_revision);
        }
      }
      return result;
    };
  };
};
